import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Tag } from '../../../common/components/detail/detail';
import { useAttention } from '../contexts/coordination';
import { ATTENTION_KINDS } from '../queries/paths';
import { onFixtures } from '../queries/source';
import { Gate } from '../../../global/contexts/auth/auth';
import { ORDER_ACTIONS, PAGE_SIZE, pageOffset } from '../utils/operator';
import { KIND_LABEL, VALIDITY_LABEL, VALIDITY_TONE, humanSeconds, label } from '../utils/format';
import ActionModal from './action-modal';
import {
    Fact,
    Facts,
    Moment,
    Origin,
    PageControls,
    RawJson,
    RefreshStatus,
    TechnicalDetails,
    Unavailable,
    Unknown,
} from './bits';

// LA BANDEJA DE ATENCIÓN — la pregunta que alguien se hace al abrir el panel:
// ¿qué me está esperando?
//
// Una incidencia se lee de arriba abajo: qué clase es, qué pasó y desde cuándo,
// sobre qué recurso, hasta cuándo vale, y qué se puede hacer. Las acciones no
// son botones inventados: cada una viene en el propio ítem, con su permiso, su
// ruta, lo que hace y lo que NO hace.
//
// EL CATÁLOGO VIAJA COMPLETO aunque una clase valga cero, así que el filtro las
// muestra todas: una clase ausente de la lista se lee como "eso no puede
// pasar", y lo que pasa es que hoy no pasó.
//
// LO QUE CORE NO SABE SE DICE. `unavailable` nombra dos cosas que no tienen
// fuente —lo que el agente puede hacer en su máquina, y la cuota del proveedor—
// y acá salen como «no se sabe», nunca como un cero ni como un campo vacío.

export default function AttentionScreen() {
    const [params, setParams] = useSearchParams();
    const kind = params.get('clase') || '';
    const offset = pageOffset(params.get('desde'));
    const query = useAttention({ kind, offset });
    const { data, origin, unavailable, isLoading, error } = query;
    const setOffset = value => {
        const next = new URLSearchParams(params);
        next.set('desde', value);
        setParams(next);
    };
    const [acting, setActing] = useState(null);

    const setKind = value => {
        const target = new URLSearchParams(params);
        target.delete('desde');
        if (value) target.set('clase', value);
        else target.delete('clase');
        setParams(target, { replace: true });
    };

    const catalogue = data?.kinds ?? {};
    const counts = data?.by_kind ?? {};
    const kinds = Object.keys(catalogue).length ? Object.keys(catalogue) : ATTENTION_KINDS;

    return (
        <section className="coord">
            <header className="coord__head">
                <div>
                    <span className="coord__hint">Bandeja de atención</span>
                    <h2 className="coord__title">Qué está esperando a una persona</h2>
                </div>
                {data && (
                    <span className="coord__count">
                        {data.pagination.total}{' '}
                        {data.pagination.total === 1 ? 'incidencia' : 'incidencias'} abiertas
                    </span>
                )}
            </header>

            <p className="coord__about">
                Revisa los problemas que necesitan tu intervención para que los agentes puedan
                continuar.
            </p>
            <RefreshStatus query={query} />
            <Origin origin={origin} />

            <div className="coord__filters" role="group" aria-label="Filtrar por clase">
                <FilterChip on={!kind} onClick={() => setKind('')}>
                    Todas{data ? ` · ${data.pagination.total}` : ''}
                </FilterChip>
                {kinds.map(key => (
                    <FilterChip key={key} on={kind === key} onClick={() => setKind(key)}>
                        {label(KIND_LABEL, key)}
                        {counts[key] ? ` · ${counts[key].total}` : ' · 0'}
                    </FilterChip>
                ))}
            </div>

            {isLoading && <p className="coord__loading">Leyendo la bandeja...</p>}
            {error && <p className="coord__error">{error.message}</p>}
            {unavailable && <Unavailable>{unavailable}</Unavailable>}

            {data && (
                <>
                    <PageControls
                        total={data.pagination.total}
                        offset={offset}
                        limit={onFixtures ? data.pagination.limit : PAGE_SIZE}
                        onChange={setOffset}
                        busy={query.isFetching}
                    />
                    {data.pagination.truncated && (
                        <p role="status" className="coord__unavailable">
                            Hay más incidencias de las que esta consulta alcanzó a revisar. Los
                            totales pueden ser parciales.
                        </p>
                    )}

                    <ul className="coord__items">
                        {data.items.map(item => (
                            <AttentionItem
                                key={item.id}
                                item={item}
                                catalogue={catalogue[item.kind]}
                                observedAt={data.observed_at}
                                onAct={action => setActing({ action, item })}
                            />
                        ))}
                    </ul>

                    {!data.items.length && (
                        <p className="coord__empty">
                            No hay incidencias en esta página. Puedes cambiar el filtro o consultar
                            los puestos.
                        </p>
                    )}

                    <TechnicalDetails label="Alcance de este diagnóstico">
                        <div className="coord__panels">
                            <Unknowable unavailable={data.unavailable} />
                            <Privacy
                                privacy={data.privacy}
                                limits={data.limits}
                                features={data.features}
                            />
                        </div>

                        <Catalogue catalogue={catalogue} counts={counts} />
                    </TechnicalDetails>

                    <RawJson value={data} />
                </>
            )}

            {acting && (
                <ActionModal
                    action={acting.action}
                    item={acting.item}
                    onClose={() => setActing(null)}
                />
            )}
        </section>
    );
}

function FilterChip({ on, onClick, children }) {
    return (
        <button
            type="button"
            className={`coord__chip${on ? ' coord__chip--on' : ''}`}
            onClick={onClick}
            aria-pressed={on}
        >
            {children}
        </button>
    );
}

// Cuántas se leyeron, cuántas hay y hasta dónde miró el servidor. `scanned` y
// `max_scan` viajan por algo: una bandeja truncada es una bandeja que no vio
// todo, y esconderlo la haría parecer completa.
function AttentionItem({ item, catalogue, observedAt, onAct }) {
    const validity = item.validity ?? {};

    return (
        <li className="coord-item">
            <header className="coord-item__head">
                <span className="coord-item__kind">{label(KIND_LABEL, item.kind)}</span>
                <Tag>{item.reason}</Tag>
                <Tag tone={VALIDITY_TONE[validity.state] ?? 'neutral'}>
                    {label(VALIDITY_LABEL, validity.state)}
                    {validity.seconds_left != null &&
                        ` · quedan ${humanSeconds(validity.seconds_left)}`}
                </Tag>
                <span className="coord-item__resource">
                    {item.resource.type} #{item.resource.id}
                </span>
            </header>

            <p className="coord-item__what">{item.happened.what}</p>

            <Facts>
                <Fact label="Desde">
                    <Moment at={item.happened.since} observedAt={observedAt} />
                </Fact>
                <Fact label="Vence">
                    {validity.expires_at ? (
                        <Moment at={validity.expires_at} observedAt={observedAt} />
                    ) : (
                        <Unknown>no vence</Unknown>
                    )}
                </Fact>
                <Fact label="Alcance">
                    <Scope scope={item.scope} />
                </Fact>
            </Facts>

            {item.happened.detail && <Detail detail={item.happened.detail} />}

            {catalogue?.unavailable && (
                <p className="coord-item__unavailable">
                    <strong>De esta clase, Core no sabe todo:</strong>{' '}
                    {catalogue.unavailable.detail}
                </p>
            )}

            {catalogue?.observed_scope === 'proceso' && (
                <p className="coord-item__unavailable">
                    Lo observado es de <strong>este proceso</strong> de Core: dice desde cuándo no
                    se confirma nada, no que el equipo no exista.
                </p>
            )}

            <div className="coord-item__actions">
                {item.actions.map(action => (
                    <Gate
                        key={action.key}
                        all={
                            ORDER_ACTIONS[action.key]
                                ? [
                                      'machines:read',
                                      ORDER_ACTIONS[action.key].permission,
                                      ...(ORDER_ACTIONS[action.key].also ?? []),
                                  ]
                                : ['tasks:read']
                        }
                    >
                        <button
                            type="button"
                            className="coord-item__action"
                            onClick={() => onAct(action)}
                            title={action.does}
                        >
                            {ORDER_ACTIONS[action.key]?.label ?? 'Ver tarea y revisión'}
                        </button>
                    </Gate>
                ))}
                {!item.actions.length && (
                    <span className="coord-item__no-action">
                        Sin acción autorizada para esta incidencia.
                    </span>
                )}
            </div>
        </li>
    );
}

// El alcance: solo lo que la incidencia trae. Un `null` no se dibuja como cero
// ni se rellena con el proyecto de al lado.
function Scope({ scope }) {
    const entries = Object.entries(scope ?? {}).filter(([, value]) => value != null);
    if (!entries.length) return <Unknown>sin alcance</Unknown>;
    return (
        <span className="coord-item__scope">
            {entries.map(([key, value]) => (
                <span key={key} className="coord-item__scope-bit">
                    {key.replace('_id', '')} {value}
                </span>
            ))}
        </span>
    );
}

// El detalle es prosa y números del propio hecho. La prosa de un agente ya
// viaja lavada (`[secreto]`, rutas recortadas, 240 caracteres): se muestra como
// vino, sin reconstruirla.
function Detail({ detail }) {
    return (
        <dl className="coord-item__detail">
            {Object.entries(detail).map(([key, value]) => (
                <div key={key}>
                    <dt>{key}</dt>
                    <dd>{value == null ? <Unknown /> : String(value)}</dd>
                </div>
            ))}
        </dl>
    );
}

function Unknowable({ unavailable }) {
    const entries = Object.entries(unavailable ?? {});
    if (!entries.length) return null;
    return (
        <section className="coord__panel">
            <h3>Lo que Core no sabe</h3>
            <p className="coord__panel-note">
                No se dibuja como cero ni como un campo vacío: no hay fuente.
            </p>
            {entries.map(([key, value]) => (
                <div key={key} className="coord__panel-row">
                    <strong>{key.replace(/_/g, ' ')}</strong>
                    <Unknown reason={value.reason}>no se sabe</Unknown>
                    <p>{value.detail}</p>
                </div>
            ))}
        </section>
    );
}

function Privacy({ privacy, limits, features }) {
    return (
        <section className="coord__panel">
            <h3>Cómo viaja lo que escribió un agente</h3>
            <p className="coord__panel-note">{privacy?.rule}</p>
            <p className="coord__panel-note">
                <strong>Nunca viaja:</strong> {privacy?.never}
            </p>
            <Facts>
                <Fact label="Tope de prosa">{limits?.prose_limit} caracteres</Fact>
                <Fact label="Tope de la cola">
                    {limits?.pending_orders_limit} órdenes por puesto
                </Fact>
                <Fact label="Página">
                    {limits?.default_limit} por omisión, {limits?.max_limit} máximo
                </Fact>
                <Fact label="Funciones">
                    {Object.entries(features ?? {})
                        .map(([key, on]) => `${key}: ${on ? 'sí' : 'no'}`)
                        .join(' · ')}
                </Fact>
            </Facts>
        </section>
    );
}

// El catálogo completo, con la clase que vale cero incluida: qué significa, de
// qué filas sale y qué acciones tiene.
function Catalogue({ catalogue, counts }) {
    const entries = Object.entries(catalogue);
    if (!entries.length) return null;
    return (
        <section className="coord__catalogue">
            <h3>Las seis clases</h3>
            <ul>
                {entries.map(([key, value]) => (
                    <li key={key}>
                        <header>
                            <strong>{label(KIND_LABEL, key)}</strong>
                            <span className="coord__catalogue-count">
                                {counts[key]?.total ?? 0} abiertas
                            </span>
                        </header>
                        <p>{value.means}</p>
                        <p className="coord__catalogue-source">
                            <strong>Sale de:</strong> {value.source}
                        </p>
                        <p className="coord__catalogue-source">
                            <strong>Razones:</strong> {value.reasons.join(' · ')}
                            {' · '}
                            <strong>Acciones:</strong> {value.actions.join(' · ')}
                        </p>
                    </li>
                ))}
            </ul>
        </section>
    );
}
